import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import type { Socket } from 'socket.io-client';
import { socket } from '../services/socket';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import {
  MessageSquare,
  LogOut,
  Bell,
  Menu,
  Building2,
  Activity,
  Shield,
  Users as UsersIcon,
  MessageCircle,
  Contact,
  Webhook,
  Loader2,
  User
} from 'lucide-react';
import { api } from '../services/api';
import StatusSelector from './StatusSelector';
import type { Notification, AgentStatus } from '../types';
import { playNotificationSound } from '../utils/notificationSound';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { UpdateAvailableModal } from './UpdateAvailableModal';

export const Layout: React.FC = () => {
  const { agent, logout, isAdmin, isDepartmentManager, updateAgent } = useAuth();
  const { systemName, systemLogo } = useSettings();
  const { showUpdateModal, handleUpdate, handleDismiss } = useVersionCheck();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<AgentStatus>(agent?.status || 'ONLINE');
  const [loadingNotificationId, setLoadingNotificationId] = useState<string | null>(null);
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const notificationTimeoutRef = useRef<number | null>(null);

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Administrador do Sistema';
      case 'DEPARTMENT_MANAGER':
        return 'Líder de Departamento';
      case 'AGENT':
        return 'Atendente';
      default:
        return role;
    }
  };

  useEffect(() => {
    // Load notifications once on mount
    loadNotifications();

    // Cleanup timeout on unmount
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  // Global WebSocket listener for notification sounds
  useEffect(() => {
    console.log('🔌 [Layout] Initializing global WebSocket for notifications');

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ [Layout] Global WebSocket connected:', socket.id);
    });

    socket.on('conversation-updated', (data: { conversationId: string; messageId?: string; senderType?: string; action?: string; agentId?: string; departmentId?: string }) => {
      console.log('🔔 [Layout] Conversation event received:', data);
      console.log('🔔 [Layout] Current agent ID:', agent?.id);
      console.log('🔔 [Layout] Current agent departmentId:', agent?.department?.id);
      console.log('🔔 [Layout] Event agentId:', data.agentId);
      console.log('🔔 [Layout] Event departmentId:', data.departmentId);
      console.log('🔔 [Layout] Event action:', data.action);
      console.log('🔔 [Layout] Event senderType:', data.senderType);

      // Play notification sound ONLY for:
      // - Conversations assigned to the logged-in agent
      if (data.action === 'assigned' && data.agentId === agent?.id) {
        console.log('🔊 [Layout] Playing notification sound - conversation assigned to me');
        playNotificationSound();
      }
      // - Conversations queued in my department
      else if (data.action === 'queued' && data.departmentId === agent?.department?.id) {
        console.log('🔊 [Layout] Playing notification sound - conversation queued in my department');
        playNotificationSound();
      }
      // - "Outros" conversations (notify all agents)
      else if (data.action === 'outros') {
        console.log('🔊 [Layout] Playing notification sound - Outros conversation');
        playNotificationSound();
      }
      // - Customer messages in conversations assigned to the logged-in agent
      else if (data.senderType === 'CUSTOMER' && data.agentId === agent?.id) {
        console.log('🔊 [Layout] Playing notification sound - customer message in my conversation');
        playNotificationSound();
      } else {
        console.log('⏭️ [Layout] Skipping notification sound - conditions not met');
        console.log('⏭️ [Layout] Condition 1 (assigned to me):', data.action === 'assigned' && data.agentId === agent?.id);
        console.log('⏭️ [Layout] Condition 2 (queued in my dept):', data.action === 'queued' && data.departmentId === agent?.department?.id);
        console.log('⏭️ [Layout] Condition 3 (outros):', data.action === 'outros');
        console.log('⏭️ [Layout] Condition 4 (customer msg):', data.senderType === 'CUSTOMER' && data.agentId === agent?.id);
      }

      // Reload notifications to show badge updates (debounced)
      loadNotificationsDebounced();
    });

    // Listen for new notifications via WebSocket
    socket.on('notification-created', (data: { agentId: string; notification: any }) => {
      console.log('🔔 [Layout] Notification event received:', data);

      // Only reload if notification is for current agent
      if (data.agentId === agent?.id) {
        console.log('📬 [Layout] Reloading notifications - new notification for me');
        loadNotifications();
      }
    });

    // Listen for agent deactivation
    socket.on('agent-deactivated', (data: { agentId: string; agentEmail: string; agentName: string }) => {
      console.log('⚠️ [Layout] Agent deactivated event received:', data);

      // If the deactivated agent is the current logged-in user, force logout
      if (data.agentId === agent?.id) {
        console.log('🚪 [Layout] Current user was deactivated - forcing logout');

        // Show alert to user
        alert('Sua conta foi desativada por um administrador. Você será desconectado agora.');

        // Logout
        logout();

        // Navigate to login
        navigate('/login');
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ [Layout] Global WebSocket disconnected:', reason);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ [Layout] Global WebSocket connection error:', error);
    });

    // Cleanup on unmount - remove listeners but don't disconnect (shared socket)
    return () => {
      console.log('🔌 [Layout] Removing WebSocket listeners');
      socket.off('connect');
      socket.off('conversation-updated');
      socket.off('notification-created');
      socket.off('agent-deactivated');
      socket.off('disconnect');
      socket.off('connect_error');
    };
  }, [agent, logout, navigate]);

  const loadNotifications = async () => {
    try {
      const data = await api.getNotifications({ isRead: 'false' });
      setNotifications(data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  // Debounced version to prevent excessive calls
  const loadNotificationsDebounced = () => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    notificationTimeoutRef.current = setTimeout(() => {
      loadNotifications();
    }, 1000); // Wait 1 second before loading
  };

  const handleMarkAsRead = async (id: string, metadata: Record<string, unknown> | null) => {
    try {
      setLoadingNotificationId(id);
      await api.markNotificationAsRead(id);
      setNotifications(notifications.filter(n => n.id !== id));
      setShowNotifications(false);

      // Navigate to conversation if conversationId is present in metadata
      if (metadata && typeof metadata === 'object' && 'conversationId' in metadata) {
        const conversationId = metadata.conversationId as string;
        navigate(`/conversations/${conversationId}`);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    } finally {
      setLoadingNotificationId(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setIsMarkingAllAsRead(true);
      await api.markAllNotificationsAsRead();
      setNotifications([]);
      setShowNotifications(false);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setIsMarkingAllAsRead(false);
    }
  };

  const handleStatusChange = async (status: AgentStatus) => {
    try {
      await api.updateAgentStatus(status);
      setCurrentStatus(status);

      // Update agent in context
      if (agent) {
        updateAgent({ ...agent, status });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const navItems = [
    { path: '/', label: 'Painel Inicial', icon: Activity },
    { path: '/conversations', label: 'Atendimentos', icon: MessageSquare },
    { path: '/contacts', label: 'Contatos', icon: Contact },
    // ...(isAdmin() || isDepartmentManager() ? [
    //   { path: '/ratings', label: 'Avaliações', icon: Star },
    // ] : []),
    { path: '/internal-chat', label: 'Chat Interno', icon: MessageCircle },
    { path: '/profile', label: 'Meu Perfil', icon: User },
    ...(isDepartmentManager() && !isAdmin() ? [
      { path: '/users', label: 'Departamento', icon: Building2 },
    ] : []),
    ...(agent?.role === 'GENERAL_MANAGER' ? [
      { path: '/users', label: 'Usuários', icon: UsersIcon },
      { path: '/departments', label: 'Departamentos', icon: Building2 },
    ] : []),
    ...(isAdmin() || isDepartmentManager() || agent?.role === 'GENERAL_MANAGER' ? [
      { path: '/satisfaction-stats', label: 'Pesquisa de Satisfação', icon: Bell },
    ] : []),
    ...(isAdmin() && agent?.role !== 'GENERAL_MANAGER' ? [
      { path: '/users', label: 'Usuários', icon: UsersIcon },
      { path: '/departments', label: 'Departamentos', icon: Building2 },
      { path: '/webhook-config', label: 'Configuração de Webhook', icon: Webhook },
      { path: '/admin-settings', label: 'Administração do Sistema', icon: Shield }
    ] : []),
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Bar */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 fixed top-0 left-0 right-0 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
            >
              <Menu className="w-5 h-5 dark:text-gray-200" />
            </button>
            <div className="flex items-center space-x-2">
              {systemLogo ? (
                <img
                  src={`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${systemLogo}`}
                  alt="Logo"
                  className="w-8 h-8 object-contain"
                />
              ) : (
                <MessageSquare className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              )}
              <span className="font-bold text-lg dark:text-white">{systemName}</span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Status Selector */}
            <StatusSelector currentStatus={currentStatus} onStatusChange={handleStatusChange} />

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition relative"
              >
                <Bell className="w-5 h-5 dark:text-gray-200" />
                {notifications.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h3 className="font-semibold dark:text-white">Notificações</h3>
                    {notifications.length > 0 && (
                      <button
                        onClick={handleMarkAllAsRead}
                        disabled={isMarkingAllAsRead}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm"
                      >
                        {isMarkingAllAsRead && <Loader2 className="w-3 h-3 animate-spin" />}
                        Marcar todas como lida
                      </button>
                    )}
                  </div>
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        Nenhuma notificação nova
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer relative ${
                            loadingNotificationId === notification.id ? 'opacity-50 pointer-events-none' : ''
                          }`}
                          onClick={() => handleMarkAsRead(notification.id, notification.metadata)}
                        >
                          {loadingNotificationId === notification.id && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                            </div>
                          )}
                          <p className="font-medium text-sm dark:text-white">{notification.title}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{notification.message}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {new Date(notification.createdAt).toLocaleString()}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-3 pl-4 border-l border-gray-200 dark:border-gray-700">
              <div className="text-right">
                <p className="text-sm font-medium dark:text-white">{agent?.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{getRoleLabel(agent?.role)}</p>
              </div>
              <button
                onClick={logout}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition text-red-600 dark:text-red-400"
                title="Sair"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside
          className={`${
            isSidebarOpen ? 'w-64' : 'w-16'
          } bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 fixed left-0 top-16 bottom-0 transition-all duration-300 overflow-hidden`}
        >
          <nav className="p-4 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={!isSidebarOpen ? item.label : undefined}
                  className={`flex items-center ${isSidebarOpen ? 'space-x-3 px-4' : 'justify-center px-2'} py-3 rounded-lg transition ${
                    isActive(item.path)
                      ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {isSidebarOpen && <span className="font-medium">{item.label}</span>}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main
          className={`flex-1 ${isSidebarOpen ? 'ml-64' : 'ml-16'} transition-all duration-300 p-6`}
        >
          <Outlet />
        </main>
      </div>

      {/* Update Available Modal */}
      {showUpdateModal && (
        <UpdateAvailableModal onUpdate={handleUpdate} onDismiss={handleDismiss} />
      )}
    </div>
  );
};
